import { Link, usePage } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { relFor } from "@/lib/affiliate"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { ExternalLink } from "lucide-react"
import { useState } from "react"
import { organizationNode, personHarryNode } from "@/lib/seo"
import type { PageProps } from "@/types/inertia"

const DESCRIPTION =
  "A curated library of the best books on podcasting — strategy, interviewing, audience growth, and monetization. Picks from someone who's launched 130+ shows."

type BookLink = {
  label: string
  url: string
}

type Book = {
  title: string
  author: string
  isbn?: string
  take: string
  cover?: string
  links: BookLink[]
  relatedPost?: { title: string; href: string }
}

type BookSection = {
  title: string
  intro: string
  books: Book[]
}

function coverUrl(isbn: string) {
  return `https://covers.openlibrary.org/b/isbn/${isbn}-M.jpg`
}

const SECTIONS: BookSection[] = [
  {
    title: "Before you launch",
    intro:
      "The decisions you make before you record episode one shape the next two years of the show. These books cover strategy, positioning, and the planning most people skip.",
    books: [
      {
        title: "Podcast Launch",
        author: "John Lee Dumas",
        isbn: "9781508418597",
        cover: coverUrl("9781508418597"),
        take:
          "The playbook JLD used to launch Entrepreneurs on Fire. Tactical and step-by-step — strong on launch mechanics, weaker on long-term strategy, but it gets you from zero to published faster than anything else on this list.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4vDArr0" },
        ],
        relatedPost: { title: "How to Start a Podcast (2026 Guide)", href: "/blog/how-to-start-a-podcast" },
      },
      {
        title: "Podcasting for Dummies",
        author: "Tee Morris & Chuck Tomasi",
        isbn: "9781394264520",
        cover: coverUrl("9781394264520"),
        take:
          "Don't let the title fool you. This is the most comprehensive single reference on the mechanics of podcasting — gear, recording, editing, publishing, workflow. Updated regularly. The one I'd hand someone who wants to understand everything before they start.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4xU8xss" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9781394264520" },
        ],
        relatedPost: { title: "Podcast Equipment Guide (2026)", href: "/blog/podcast-equipment-guide" },
      },
      {
        title: "Big Podcast",
        author: "David Hooper",
        isbn: "9781608428885",
        cover: coverUrl("9781608428885"),
        take:
          "Positioning-first. Hooper's argument is that most podcasts fail because they're built around content, not audience. The chapters on narrowing your topic and defining your listener are worth the price alone.",
        links: [
          { label: "Amazon", url: "https://amzn.to/3QS0GuS" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9781608428885" },
        ],
        relatedPost: { title: "Podcast Marketing Strategy for Founders", href: "/blog/podcast-marketing-strategy" },
      },
      {
        title: "NPR's Podcast Start Up Guide",
        author: "Glen Weldon",
        isbn: "9781984862518",
        cover: coverUrl("9781984862518"),
        take:
          "NPR's own playbook, featuring advice from the hosts of How I Built This, Code Switch, and Pop Culture Happy Hour. If you want to understand how the best audio organization in the world thinks about launching shows, this is it.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4v3g8Cc" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9781984862518" },
        ],
      },
      {
        title: "Make Noise",
        author: "Eric Nuzum",
        isbn: "9781523504558",
        cover: coverUrl("9781523504558"),
        take:
          "Nuzum launched 130+ shows at NPR and Audible. His framework for finding your creative voice and building a show that listeners connect with is the closest thing to a creative director's manual for podcasting. The 'ten-word description' exercise alone is worth the read.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4wkeiOx" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9781523504558" },
        ],
      },
    ],
  },
  {
    title: "The craft of interviewing",
    intro:
      "The conversation is the product. These books won't teach you how to use a mic — they'll teach you how to make a guest say something they've never said on any other show.",
    books: [
      {
        title: "Talk to Me",
        author: "Dean Nelson",
        isbn: "9780062825209",
        cover: coverUrl("9780062825209"),
        take:
          "The best book on interviewing I've read, period. Nelson trained journalists for decades, and his framework — listen for the surprise, follow the energy, sit in the silence — changed how I approach every conversation. Required reading for anyone who interviews people for a living.",
        links: [
          { label: "Amazon", url: "https://amzn.to/440RQxU" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9780062825209" },
        ],
      },
      {
        title: "The Coaching Habit",
        author: "Michael Bungay Stanier",
        isbn: "9780978440749",
        cover: coverUrl("9780978440749"),
        take:
          "Not a podcasting book. But the seven questions framework — especially 'And what else?' and 'What's the real challenge here for you?' — will make you a better interviewer than any podcast-specific book. The skill of staying curious instead of jumping to the next question is the whole game.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4gakpjJ" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9780978440749" },
        ],
      },
      {
        title: "Creative Calling",
        author: "Chase Jarvis",
        isbn: "9780062879967",
        cover: coverUrl("9780062879967"),
        take:
          "Jarvis built CreativeLive and has thousands of conversations under his belt. This book is about the creative practice underneath all creative work — including the conversation itself. Read it when you feel like your interviews are getting formulaic.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4v3glW0" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9780062879967" },
        ],
      },
      {
        title: "Crucial Conversations",
        author: "Kerry Patterson, Joseph Grenny, et al.",
        isbn: "9781260474183",
        cover: coverUrl("9781260474183"),
        take:
          "The textbook on high-stakes dialogue. Not about podcasting, but the framework for making hard conversations safe to have translates directly to interviews where you want to go deeper than surface-level answers.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4wjbbX8" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9781260474183" },
        ],
      },
    ],
  },
  {
    title: "Growing the show",
    intro:
      "Downloads are a vanity metric. These books focus on what actually builds a podcast audience — positioning, consistency, repurposing, and the guest strategy.",
    books: [
      {
        title: "Superfans",
        author: "Pat Flynn",
        isbn: "9781949709469",
        cover: coverUrl("9781949709469"),
        take:
          "Flynn's thesis is that a small, deeply engaged audience beats a large passive one every time. For podcasters: don't chase downloads, build a community around the show. The pyramid model — casual → active → connected → superfan — maps directly to the podcast funnel.",
        links: [
          { label: "Amazon", url: "https://amzn.to/3QLMZh4" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9781949709469" },
        ],
      },
      {
        title: "This Is Marketing",
        author: "Seth Godin",
        isbn: "9780525540830",
        cover: coverUrl("9780525540830"),
        take:
          "Not about podcasting. But Godin's 'smallest viable audience' and 'people like us do things like this' reframes everything about who you're making the show for. If your podcast is trying to be for everyone, read this first.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4eFmfrO" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9780525540830" },
        ],
      },
      {
        title: "The Audience Is Listening",
        author: "Tom Webster",
        isbn: "9781774585276",
        cover: coverUrl("9781774585276"),
        take:
          "Webster spent 30 years studying audio audiences at Edison Research. This is the most data-informed book on what podcast listeners actually want — not what creators think they want. Short, research-backed, and genuinely useful for anyone wondering why their show isn't growing.",
        links: [
          { label: "Amazon", url: "https://amzn.to/44JjsaV" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9781774585276" },
        ],
      },
      {
        title: "The Audacity to Podcast",
        author: "Daniel J. Lewis",
        isbn: "9780988466302",
        cover: coverUrl("9780988466302"),
        take:
          "Lewis has been in the podcasting space since 2007 and runs one of the longest-standing shows about the craft. This is the practitioner's companion — practical, specific, and written by someone who's lived through every platform shift and format trend. Strong on the day-to-day decisions that keep a show alive past the first year.",
        links: [
          { label: "Amazon", url: "https://amzn.to/3SG8IYg" },
        ],
      },
      {
        title: "Content Inc.",
        author: "Joe Pulizzi",
        isbn: "9781264257546",
        cover: coverUrl("9781264257546"),
        take:
          "Pulizzi's argument: build the audience before the product. A podcast is the perfect content-first vehicle for this model. Strong on the content-tilt concept — the specific angle that makes your content unique — which is exactly what separates shows that grow from shows that don't.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4xTugAV" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9781264257546" },
        ],
        relatedPost: { title: "How to Repurpose Podcast Content", href: "/blog/repurpose-podcast-content" },
      },
    ],
  },
  {
    title: "Monetization & the business model",
    intro:
      "The podcast isn't the business. It's the relationship engine that feeds the business. These books cover how to turn the show into revenue — through sponsorship, products, services, or the relationships themselves.",
    books: [
      {
        title: "Profit from Your Podcast",
        author: "Dave Jackson",
        isbn: "9781621537724",
        cover: coverUrl("9781621537724"),
        take:
          "Jackson has been teaching podcasting since 2005 and this is the most practical monetization guide on the list. Covers sponsorship, affiliate marketing, crowdfunding, premium content, and the math behind each. No hype, just what actually works at different audience sizes.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4eF4wR6" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9781621537724" },
        ],
        relatedPost: { title: "How Much Does a Podcast Cost?", href: "/blog/podcast-production-cost" },
      },
      {
        title: "Will It Fly?",
        author: "Pat Flynn",
        isbn: "9780997082302",
        cover: coverUrl("9780997082302"),
        take:
          "Validates the business idea before you build it. For podcasters who want to launch a product or service off the back of their show, Flynn's validation framework prevents the classic mistake of building something your audience doesn't want.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4fcllTG" },
        ],
      },
      {
        title: "Expert Secrets",
        author: "Russell Brunson",
        isbn: "9781683504580",
        cover: coverUrl("9781683504580"),
        take:
          "Love him or not, Brunson's framework for packaging expertise into offers is useful. If your podcast positions you as the expert, this book shows how to build the funnel behind it. Take the positioning chapters seriously; skip the clickfunnel-specific tactics unless that's your stack.",
        links: [
          { label: "Amazon", url: "https://amzn.to/3St9mIG" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9781401970604" },
        ],
      },
      {
        title: "Company of One",
        author: "Paul Jarvis",
        isbn: "9780358213253",
        cover: coverUrl("9780358213253"),
        take:
          "The case for not scaling. Most expert podcasters don't need a media empire — they need a lean, profitable operation where the show feeds a small number of high-value client relationships. Jarvis gives permission to stay small and make it work.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4eF1Kvk" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9780358213253" },
        ],
      },
    ],
  },
  {
    title: "The bigger picture",
    intro:
      "These aren't podcasting books. They're the books that shaped how I think about conversations, storytelling, and building something that compounds. If you only read three books from this entire page, pick from here.",
    books: [
      {
        title: "Out on the Wire",
        author: "Jessica Abel",
        isbn: "9780385348430",
        cover: coverUrl("9780385348430"),
        take:
          "A graphic-novel deep dive into how the best audio storytellers work — This American Life, Radiolab, The Moth, Planet Money. Abel sat in their edit rooms and mapped their creative process. The chapter on finding the 'X and Y' of a story changed how I think about episode structure.",
        links: [
          { label: "Amazon", url: "https://amzn.to/3SEF19Z" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9780385348430" },
        ],
      },
      {
        title: "Building a StoryBrand",
        author: "Donald Miller",
        isbn: "9781400201839",
        cover: coverUrl("9781400201839"),
        take:
          "Miller's framework: your customer is the hero, you're the guide. Applies directly to every podcast intro, every guest introduction, and every CTA. If your show's messaging feels muddled, this is where to start.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4vIlu7g" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9781400248872" },
        ],
      },
      {
        title: "Show Your Work!",
        author: "Austin Kleon",
        isbn: "9780606356398",
        cover: coverUrl("9780606356398"),
        take:
          "A short, visual argument for sharing your process. A podcast is the ultimate 'show your work' vehicle — every episode is a public record of you thinking out loud with interesting people. Kleon's framing gives you permission to share before you feel ready.",
        links: [
          { label: "Amazon", url: "https://amzn.to/449nTvA" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9780606356398" },
        ],
      },
      {
        title: "Essentialism",
        author: "Greg McKeown",
        isbn: "9780804137409",
        cover: coverUrl("9780804137409"),
        take:
          "The disciplined pursuit of less. For podcasters who are doing too much — too many episodes, too many formats, too many platforms — McKeown's framework helps you cut to the thing that actually matters. Sometimes the best podcast strategy is to do fewer things better.",
        links: [
          { label: "Amazon", url: "https://amzn.to/4vg7FMs" },
          { label: "Bookshop", url: "https://bookshop.org/a/84918/9780804137409" },
        ],
      },
    ],
  },
]

function bookListJsonLd(): Record<string, unknown> {
  let position = 0
  return {
    "@type": "ItemList",
    name: "The Podcasting Bookshelf",
    description: DESCRIPTION,
    numberOfItems: SECTIONS.reduce((n, s) => n + s.books.length, 0),
    itemListElement: SECTIONS.flatMap((section) =>
      section.books.map((book) => {
        position += 1
        return {
          "@type": "ListItem",
          position,
          item: {
            "@type": "Book",
            name: book.title,
            author: { "@type": "Person", name: book.author },
            ...(book.isbn ? { isbn: book.isbn } : {}),
            ...(book.cover ? { image: book.cover } : {}),
          },
        }
      }),
    ),
  }
}

function BookCover({ src, alt }: { src?: string; alt: string }) {
  const [failed, setFailed] = useState(false)
  if (!src || failed) return null
  return (
    <img
      src={src}
      alt={`Cover of ${alt}`}
      onError={() => setFailed(true)}
      className="h-36 w-auto shrink-0 rounded-md shadow-sm"
    />
  )
}

export default function Books() {
  const base = (usePage<PageProps>().props.app_url || "https://fullcast.co").replace(/\/$/, "")

  return (
    <MarketingLayout>
      <SeoHead
        title="The Podcasting Bookshelf"
        description={DESCRIPTION}
        image="/og-books.png"
        jsonLd={[organizationNode(base), personHarryNode(base), bookListJsonLd()]}
      />

      <Section containerClassName="max-w-3xl">
        <Eyebrow>FullCast · Curated reading</Eyebrow>
        <h1 className="display-1 mt-4">The Podcasting Bookshelf</h1>
        <p className="mt-5 text-lg text-ink-body">
          Books I come back to. Some are about podcasting. Some are about
          interviewing, marketing, or building a business around expertise. All
          of them shaped how FullCast works and how I think about shows.
        </p>
        <p className="mt-3 text-sm text-ink-muted">
          Some links are affiliate links — if you buy through them, we may earn
          a small commission at no cost to you. See our{" "}
          <Link href="/disclosure" className="text-accent-display">
            affiliate disclosure
          </Link>{" "}
          for what that means.
        </p>
      </Section>

      {SECTIONS.map((section) => (
        <Section
          key={section.title}
          containerClassName="max-w-3xl"
          className="border-t border-hairline"
        >
          <h2 className="display-2">{section.title}</h2>
          <p className="mt-3 text-ink-body">{section.intro}</p>
          <div className="mt-8 space-y-6">
            {section.books.map((book) => (
              <div
                key={book.title}
                className="flex gap-5 rounded-xl border border-hairline bg-surface p-6"
              >
                <BookCover src={book.cover} alt={book.title} />
                <div className="min-w-0 flex-1">
                  <h3 className="m-0">{book.title}</h3>
                  <p className="mt-1 text-sm text-ink-muted">by {book.author}</p>
                  <p className="mt-3 text-ink-body">{book.take}</p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {book.links.map((link) => (
                      <a
                        key={link.label}
                        href={link.url}
                        target="_blank"
                        rel={relFor(link.url)}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-display no-underline hover:underline"
                      >
                        {link.label}
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ))}
                  </div>
                  {book.relatedPost && (
                    <p className="mt-3 text-sm text-ink-muted">
                      Related:{" "}
                      <Link href={book.relatedPost.href} className="text-accent-display">
                        {book.relatedPost.title}
                      </Link>
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>
      ))}

      <Section containerClassName="max-w-2xl" className="border-t border-hairline">
        <Eyebrow>Want the short version?</Eyebrow>
        <h2 className="display-2 mt-4">We read these so our clients don't have to.</h2>
        <p className="mt-3 text-ink-body">
          The FullCast Podcast Growth Partnership builds on the ideas in these
          books — strategy, interviewing, positioning, growth — and runs the
          whole thing for you. Done-for-you podcast production for founders and
          experts who want a show that compounds.
        </p>
        <div className="mt-6">
          <Link
            href="/apply"
            className="inline-block rounded-xl bg-ink-display px-6 py-3 text-page no-underline hover:bg-ink-body"
          >
            Apply for a Fit Call →
          </Link>
        </div>
      </Section>
    </MarketingLayout>
  )
}
